/**
 * "What we'll take from it": the six lines read from a picture. Each can be
 * typed over in place; while the picture is read the list is a quiet shimmer,
 * and a picture that could not be read says so.
 */
import { useState } from "react";
import type { StructureBrief } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { BRIEF_LINES } from "./briefLines";
import type { ReadState } from "./useShotReads";

export function ShotLines({
  letter,
  brief,
  state,
  onEdit,
}: {
  letter: string;
  brief?: StructureBrief;
  state?: ReadState["state"];
  /** Given, a line can be typed over; left out, the lines are only read. */
  onEdit?: (line: keyof StructureBrief, text: string) => void;
}) {
  const [editing, setEditing] = useState<keyof StructureBrief | null>(null);
  if (!brief && !state) return null;
  const finish = (line: keyof StructureBrief, typed: string) => {
    setEditing(null);
    const text = typed.trim();
    if (brief && text && text !== brief[line]) onEdit?.(line, text);
  };
  return (
    <section className="nbtake" aria-label={`What we’ll take from picture ${letter}`}>
      <h4 className="nbtake-h">
        <Icon name="sparkle" /> What we’ll take from it
      </h4>
      {state === "failed" ? (
        <p className="nbtake-err" role="alert">
          <Icon name="info" />
          <span>Couldn’t read this picture. Try another, or design without it.</span>
        </p>
      ) : null}
      {state === "reading" ? (
        <div className="nbtake-wait" role="status">
          <span>Reading the page</span>
          {BRIEF_LINES.map((l) => (
            <i key={l.key} aria-hidden="true" />
          ))}
        </div>
      ) : null}
      {brief && !state ? (
        <ul className="nbtake-l">
          {BRIEF_LINES.map((l, i) => (
            <li key={l.key} style={{ "--i": i } as React.CSSProperties} className={cx(editing === l.key && "edit")}>
              <span className="nbtake-ic">
                <Icon name={l.icon} />
              </span>
              <div className="nbtake-b">
                <span className="nbtake-k">{l.label}</span>
                {editing === l.key && onEdit ? (
                  <textarea dir="auto"
                    className="inp"
                    rows={3}
                    maxLength={220}
                    autoFocus
                    defaultValue={brief[l.key]}
                    aria-label={`${l.label}, picture ${letter}`}
                    onBlur={(e) => finish(l.key, e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setEditing(null);
                      } else if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        finish(l.key, e.currentTarget.value);
                      }
                    }}
                  />
                ) : onEdit ? (
                  <button type="button" className="nbtake-t" aria-label={`${l.label}: ${brief[l.key]}. Change it`} onClick={() => setEditing(l.key)}>
                    {brief[l.key]}
                    <Icon name="pencil" />
                  </button>
                ) : (
                  <p className="nbtake-t">{brief[l.key]}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
